import { Outlet, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isValidLocale, isValidLanguage } from '@/lib/router'
import { LocaleRedirect } from './LocaleRedirect'

/**
 * Wraps all locale-prefixed routes. Validates :locale/:lang params and syncs
 * the URL language with i18next so the rest of the app always reads from hooks.
 */
export function LocaleLayout() {
  const { locale, lang } = useParams<{ locale: string; lang: string }>()
  const { i18n } = useTranslation()
  const isValid = isValidLocale(locale) && isValidLanguage(lang)

  // Sync i18next language with URL param (only when locale/lang are valid)
  useEffect(() => {
    if (!isValid || !lang || i18n.language === lang) return
    void i18n.changeLanguage(lang)
  }, [lang, i18n, isValid])

  if (!isValid) {
    // `/:locale/:lang` also ambiguously matches any bare 2-segment path (e.g.
    // /account/claimed-offers, /streams/:slug) — React Router has no way to
    // constrain the pattern to real locale/lang values. Route here via
    // LocaleRedirect (reads the real location.pathname) instead of a
    // hardcoded Navigate to "/us/en/", which was silently discarding the
    // entire path for any bare 2-segment URL.
    return <LocaleRedirect />
  }

  return <Outlet />
}
